/**
 * What a built fix round is asking a person to push: how the change sits on
 * the pull request, what the round says it did with each finding, and the
 * files outside the plan that pushing would also approve. The diff itself is
 * under Development in this case file.
 */
import type { JsonObject } from "@yagni/shared";

const SHAPE_LINE: Record<string, string> = {
  fix: "One update to the pull request.",
  merge: "One update to the pull request that also merges its base and resolves the conflict.",
  merge_then_fix: "Merges the pull request's base, then one fix on top.",
};

const OUTCOME_WORD: Record<string, string> = {
  fixed: "Fixed",
  disputed: "Disputed",
  not_reproducible: "Not reproduced",
  needs_person: "For a person",
};

interface Outcome {
  id: string;
  outcome: string;
  reason: string | null;
}

function outcomesOf(evidence: JsonObject): Outcome[] {
  const raw = evidence.outcomes;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const row = entry as Record<string, unknown>;
    return typeof row.id === "string" && typeof row.outcome === "string"
      ? [{ id: row.id, outcome: row.outcome, reason: typeof row.reason === "string" ? row.reason : null }]
      : [];
  });
}

function findingLabels(evidence: JsonObject): Map<string, string> {
  const labels = new Map<string, string>();
  if (!Array.isArray(evidence.findings)) return labels;
  for (const entry of evidence.findings) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const row = entry as Record<string, unknown>;
    if (typeof row.id !== "string" || typeof row.body !== "string") continue;
    const body = row.body.replace(/\s+/g, " ").trim();
    labels.set(row.id, body.length > 90 ? `${body.slice(0, 90)}…` : body);
  }
  return labels;
}

export function PatchRoundEvidence({ evidence }: { evidence: JsonObject }) {
  const shape = typeof evidence.shape === "string" ? SHAPE_LINE[evidence.shape] : null;
  const outcomes = outcomesOf(evidence);
  const labels = findingLabels(evidence);
  const extraFiles = Array.isArray(evidence.extraFiles)
    ? evidence.extraFiles.filter((path): path is string => typeof path === "string")
    : [];
  return (
    <div className="wk-gate-ask" data-testid="patch-round-evidence">
      {shape && <p className="wk-ni-title">{shape}</p>}
      {outcomes.length > 0 && (
        <ul className="wk-patch-outcomes" data-testid="patch-round-outcomes">
          {outcomes.map((outcome) => (
            <li key={outcome.id} data-outcome={outcome.outcome}>
              <strong>{OUTCOME_WORD[outcome.outcome] ?? outcome.outcome}</strong>
              {" · "}
              {labels.get(outcome.id) ?? outcome.id}
              {outcome.reason ? ` — ${outcome.reason}` : ""}
            </li>
          ))}
        </ul>
      )}
      {extraFiles.length > 0 && (
        <p data-testid="patch-round-extra-files">
          Pushing also approves {extraFiles.length === 1 ? "a file" : `${extraFiles.length} files`} outside the plan:{" "}
          {extraFiles.slice(0, 8).map((path, index) => (
            <span key={path}>
              {index > 0 ? ", " : ""}
              <code>{path}</code>
            </span>
          ))}
          {extraFiles.length > 8 ? ` and ${extraFiles.length - 8} more` : ""}.
        </p>
      )}
    </div>
  );
}
